import { useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

// The seven categories of outbound communication Semmy can handle
export type ActionType =
  | 'first_contact'
  | 'follow_up'
  | 'quote_communication'
  | 'scheduling'
  | 'complaint_response'
  | 'relationship_close'
  | 'general_communication';

export type ClientStatus = 'new' | 'active' | 'at_risk' | 'inactive';
export type ActionStatus = 'queued' | 'approved' | 'rejected' | 'sent';
export type AuthorityStatus = 'supervised' | 'proposal_queued' | 'autonomous' | 'suspended';

// Five binary signals that feed into the stakes scoring formula
export interface StakesInputs {
  contains_numbers: boolean;          // prices, dates, quantities
  contains_commitment: boolean;       // promises about delivery, timing, etc.
  contains_legal_or_regulatory: boolean;
  client_sentiment_negative: boolean; // sentiment_score < 0.4
  client_is_new: boolean;
}

// The full action record — includes both the draft and the trust manifest
export interface SemmyAction {
  id: string;
  created_at: string;
  updated_at: string;
  action_type: ActionType;
  status: ActionStatus;
  draft_content: string;
  inbound_content?: string | null;  // the client message that triggered this action
  approved_content: string | null;  // what actually went out (may differ)
  client_id: string;
  client_name: string;
  client_email: string | null;
  client_phone?: string | null;
  client_status: ClientStatus;
  source?: string | null;
  sentiment_score: number;          // 0–1, higher = more positive
  stakes_score: number;             // 0–10, computed from stakes_inputs
  stakes_inputs: StakesInputs;
  requires_approval: boolean;
  approval_reason: string | null;
  autonomous_send: boolean;
  approved_at: string | null;
  rejected_at: string | null;
  sent_at: string | null;
  rejection_reason: string | null;
  trigger_condition: string | null;
  scheduled_for: string | null;
  partner_id: string;
  facebook_created_at?: string | null;
}

// An action Semmy considered but chose not to take, with explicit reasoning
export interface SemmyDeferred {
  id: string;
  created_at: string;
  considered_action: string;
  client_id: string;
  client_name: string;
  deferral_reason: string;
  recommended_next_action: string;
  recommended_trigger: string;
  outcome_validated: boolean | null;
  outcome_notes: string | null;
  partner_id: string;
}

// Logged whenever Dustin edits a message before approving
// These are the training signal for improving Semmy's judgment
export interface SemmyCorrection {
  id: string;
  created_at: string;
  action_id: string;
  original_text: string;
  approved_text: string;
  diff: string;
  correction_category: string;  // 'tone' | 'pricing' | 'commitment_language' | 'relationship_read' | 'factual'
  partner_id: string;
  client_id?: string;
  correction_note?: string;
  correction_type?: string;
  was_significant?: boolean;
}

// Per-action-type performance and earned autonomy tracking
export interface SemmyAuthority {
  id: string;
  action_type: ActionType;
  total_proposed: number;
  approved_without_edit: number;
  approved_with_edit: number;
  rejected: number;
  consecutive_clean_approvals: number;
  authority_proposal_threshold: number;
  authority_status: AuthorityStatus;
  partner_id: string;
}

// Weekly performance summary for the results view
export interface SemmyPerformance {
  enquiries_this_week: number;
  approved: number;
  autonomous: number;
  quotes_sent: number;
  corrections_this_week: number;
  approval_rate: number;
  autonomous_rate: number;
}

// ── Pure: stakes scoring ───────────────────────────────────────────────────────
// Every point in this formula has a clear reason — change it by changing the inputs.
export function calculateStakes(
  inputs: StakesInputs,
  actionType: ActionType,
  approvalThreshold = 7,
): { score: number; requiresApproval: boolean; reason: string } {
  let score = 0;
  const reasons: string[] = [];

  if (inputs.contains_numbers) { score += 2; reasons.push('bevat nummers'); }
  if (inputs.contains_commitment) { score += 2; reasons.push('bevat toezegging'); }
  if (inputs.client_sentiment_negative) { score += 2; reasons.push('negatief klantsentiment'); }
  if (inputs.client_is_new) { score += 2; reasons.push('klant is nieuw'); }
  if (inputs.contains_legal_or_regulatory) { score += 2; reasons.push('juridische/regelgevende taal'); }

  const alwaysReview = actionType === 'first_contact' || actionType === 'complaint_response';
  const highStakes = score >= approvalThreshold;
  const requiresApproval = alwaysReview || highStakes;

  const reason = alwaysReview
    ? `${actionType === 'first_contact' ? 'Eerste contact' : 'Klachtrespons'} altijd beoordeeld`
    : highStakes
      ? `Hoog risico (${score}/10): ${reasons.join(', ')}`
      : null;

  return { score, requiresApproval, reason: reason || '' };
}

// ── Mock data ──────────────────────────────────────────────────────────────────
// Realistic demo data for a Dutch flooring company. Used when Supabase tables
// don't exist yet (migration not run) or Supabase is not configured.

const NOW = new Date('2026-05-12T10:00:00Z').getTime();
const da = (n: number) => new Date(NOW - n * 86400000).toISOString();
const ha = (n: number) => new Date(NOW - n * 3600000).toISOString();

const MOCK_QUEUED: SemmyAction[] = [
  {
    id: 'sq-1',
    created_at: ha(2),
    updated_at: ha(2),
    action_type: 'first_contact',
    status: 'queued',
    draft_content: 'Beste Marco,\n\nBedankt voor uw aanvraag via onze website! Ik heb uw wensen bekeken: 42m² PVC-vloer in de woonkamer en hal. Dit is precies ons specialisme.\n\nMag ik morgenochtend even bellen om de details te bespreken en een afspraak in te plannen voor een vrijblijvende inmeting?\n\nMet vriendelijke groet,\noperator',
    approved_content: null,
    client_id: 'client-marco',
    client_name: 'Marco Pietersen',
    client_email: 'marco.pietersen@gmail.com',
    client_status: 'new',
    sentiment_score: 0.75,
    stakes_score: 2,
    stakes_inputs: {
      contains_numbers: false,
      contains_commitment: false,
      contains_legal_or_regulatory: false,
      client_sentiment_negative: false,
      client_is_new: true,
    },
    requires_approval: true,
    approval_reason: 'First contact always reviewed',
    autonomous_send: false,
    approved_at: null,
    rejected_at: null,
    sent_at: null,
    rejection_reason: null,
    trigger_condition: 'manual',
    scheduled_for: null,
    partner_id: 'partner-003',
  },
  {
    id: 'sq-2',
    created_at: ha(1),
    updated_at: ha(1),
    action_type: 'complaint_response',
    status: 'queued',
    draft_content: 'Beste Hans,\n\nIk begrijp uw frustratie over de vertraging volledig en bied daarvoor mijn oprechte excuses aan. Dit is niet de standaard die wij nastreven.\n\nIk heb persoonlijk contact gehad met ons installatieteam. Uw vloer wordt volgende week donderdag 21 mei gelegd — dit is een vaste afspraak. Als compensatie voor het ongemak bied ik u 5% korting aan op uw eindfactuur.\n\nIk neem u morgen zelf even op om dit te bevestigen.\n\nMet vriendelijke groet,\noperator',
    approved_content: null,
    client_id: 'client-hans',
    client_name: 'Hans Kuipers',
    client_email: 'h.kuipers@outlook.com',
    client_status: 'at_risk',
    sentiment_score: 0.28,
    stakes_score: 6,
    stakes_inputs: {
      contains_numbers: true,
      contains_commitment: true,
      contains_legal_or_regulatory: false,
      client_sentiment_negative: true,
      client_is_new: false,
    },
    requires_approval: true,
    approval_reason: 'Complaint response always reviewed',
    autonomous_send: false,
    approved_at: null,
    rejected_at: null,
    sent_at: null,
    rejection_reason: null,
    trigger_condition: 'manual',
    scheduled_for: null,
    partner_id: 'partner-003',
  },
  {
    id: 'sq-3',
    created_at: ha(3),
    updated_at: ha(3),
    action_type: 'quote_communication',
    status: 'queued',
    draft_content: 'Beste Sandra,\n\nHierbij ontvangt u uw offerte voor de levering en plaatsing van 48m² eiken PVC-vloer inclusief legservice en ondervloer.\n\nTotaalbedrag: €4.680 inclusief BTW.\n\nDeze offerte is 30 dagen geldig. Na akkoord plannen we de installatie binnen 2 weken in. Op al onze installaties geven we 5 jaar garantie conform de Consumentenwetgeving.\n\nBij vragen sta ik graag voor u klaar.\n\nMet vriendelijke groet,\noperator',
    approved_content: null,
    client_id: 'client-sandra',
    client_name: 'Sandra Meijer',
    client_email: 'smeijer@hotmail.com',
    client_status: 'new',
    sentiment_score: 0.8,
    stakes_score: 8,
    stakes_inputs: {
      contains_numbers: true,
      contains_commitment: true,
      contains_legal_or_regulatory: true,
      client_sentiment_negative: false,
      client_is_new: true,
    },
    requires_approval: true,
    approval_reason: 'High stakes (8/10): contains numbers, contains commitment, legal/regulatory language, client is new',
    autonomous_send: false,
    approved_at: null,
    rejected_at: null,
    sent_at: null,
    rejection_reason: null,
    trigger_condition: 'manual',
    scheduled_for: null,
    partner_id: 'partner-003',
  },
];

const MOCK_COMPLETED: SemmyAction[] = [
  {
    id: 'sc-1',
    created_at: da(5),
    updated_at: da(5),
    action_type: 'scheduling',
    status: 'sent',
    draft_content: 'Beste Jan, ik wil de installatie graag inplannen op dinsdag 19 mei aanstaande. Schikt dit? Onze monteur is er dan de hele dag.',
    approved_content: null,
    client_id: 'client-jan',
    client_name: 'Jan de Groot',
    client_email: 'jan.degroot@gmail.com',
    client_status: 'active',
    sentiment_score: 0.82,
    stakes_score: 2,
    stakes_inputs: { contains_numbers: true, contains_commitment: false, contains_legal_or_regulatory: false, client_sentiment_negative: false, client_is_new: false },
    requires_approval: false,
    approval_reason: null,
    autonomous_send: true,
    approved_at: da(5),
    rejected_at: null,
    sent_at: da(5),
    rejection_reason: null,
    trigger_condition: null,
    scheduled_for: null,
    partner_id: 'partner-003',
  },
  {
    id: 'sc-2',
    created_at: da(4),
    updated_at: da(4),
    action_type: 'scheduling',
    status: 'sent',
    draft_content: 'Beste Marie, uw installatie staat gepland voor maandag 18 mei. We beginnen om 08:30. Tot dan!',
    approved_content: null,
    client_id: 'client-marie',
    client_name: 'Marie Hendriks',
    client_email: null,
    client_status: 'active',
    sentiment_score: 0.9,
    stakes_score: 2,
    stakes_inputs: { contains_numbers: true, contains_commitment: false, contains_legal_or_regulatory: false, client_sentiment_negative: false, client_is_new: false },
    requires_approval: false,
    approval_reason: null,
    autonomous_send: true,
    approved_at: da(4),
    rejected_at: null,
    sent_at: da(4),
    rejection_reason: null,
    trigger_condition: null,
    scheduled_for: null,
    partner_id: 'partner-003',
  },
  {
    id: 'sc-3',
    created_at: da(3),
    updated_at: da(3),
    action_type: 'follow_up',
    status: 'sent',
    draft_content: 'Beste Emma, hoe bevalt uw nieuwe vloer na drie weken? Wij hopen dat u er veel plezier van heeft! Als u vrienden of familie kent die ook op zoek zijn naar een nieuwe vloer, stellen wij het op prijs als u ons aanbeveelt.',
    approved_content: null,
    client_id: 'client-emma',
    client_name: 'Emma van Dijk',
    client_email: null,
    client_status: 'active',
    sentiment_score: 0.88,
    stakes_score: 0,
    stakes_inputs: { contains_numbers: false, contains_commitment: false, contains_legal_or_regulatory: false, client_sentiment_negative: false, client_is_new: false },
    requires_approval: false,
    approval_reason: null,
    autonomous_send: false,
    approved_at: da(3),
    rejected_at: null,
    sent_at: da(3),
    rejection_reason: null,
    trigger_condition: null,
    scheduled_for: null,
    partner_id: 'partner-003',
  },
  {
    id: 'sc-4',
    created_at: da(1),
    updated_at: da(1),
    action_type: 'follow_up',
    status: 'sent',
    // Dustin changed this from formal to informal
    draft_content: 'Geachte mevrouw Smit, ik neem contact op om te informeren of u reeds de kans heeft gehad de offerte te bekijken die wij u hebben toegezonden.',
    approved_content: 'Beste Lisa, heb je al even de kans gehad om de offerte te bekijken? Laat me even weten als je vragen hebt — ik help je graag!',
    client_id: 'client-lisa',
    client_name: 'Lisa Smit',
    client_email: null,
    client_status: 'active',
    sentiment_score: 0.68,
    stakes_score: 0,
    stakes_inputs: { contains_numbers: false, contains_commitment: false, contains_legal_or_regulatory: false, client_sentiment_negative: false, client_is_new: false },
    requires_approval: false,
    approval_reason: null,
    autonomous_send: false,
    approved_at: da(1),
    rejected_at: null,
    sent_at: da(1),
    rejection_reason: null,
    trigger_condition: null,
    scheduled_for: null,
    partner_id: 'partner-003',
  },
  {
    id: 'sc-5',
    created_at: ha(6),
    updated_at: ha(6),
    action_type: 'general_communication',
    status: 'sent',
    draft_content: 'Beste Peter, bedankt voor uw tijd gisteren. Het was een plezierig gesprek. Ik hoor graag van u zodra u een beslissing heeft genomen. Fijne dag!',
    approved_content: null,
    client_id: 'client-peter',
    client_name: 'Peter Bakker',
    client_email: null,
    client_status: 'active',
    sentiment_score: 0.91,
    stakes_score: 0,
    stakes_inputs: { contains_numbers: false, contains_commitment: false, contains_legal_or_regulatory: false, client_sentiment_negative: false, client_is_new: false },
    requires_approval: false,
    approval_reason: null,
    autonomous_send: false,
    approved_at: ha(6),
    rejected_at: null,
    sent_at: ha(6),
    rejection_reason: null,
    trigger_condition: null,
    scheduled_for: null,
    partner_id: 'partner-003',
  },
];

const MOCK_DEFERRED: SemmyDeferred[] = [
  {
    id: 'sd-1',
    created_at: da(2),
    considered_action: 'Stuur een derde opvolgbericht naar Thomas Visser',
    client_id: 'client-thomas',
    client_name: 'Thomas Visser',
    deferral_reason: 'Thomas heeft niet gereageerd op twee eerdere berichten (4 en 11 dagen geleden). Een derde bericht riskeert als opdringerig te worden ervaren en kan de relatie beschadigen.',
    recommended_next_action: 'Wacht tot Thomas zelf contact opneemt. Als er over 30 dagen nog geen reactie is, overweeg dan een vriendelijk afrondingsbericht.',
    recommended_trigger: 'Als Thomas reageert, of over 30 dagen',
    outcome_validated: null,
    outcome_notes: null,
    partner_id: 'partner-003',
  },
  {
    id: 'sd-2',
    created_at: da(1),
    considered_action: 'Stuur herziening offerte aan Maria Janssen met 10% vroegboekkorting',
    client_id: 'client-maria',
    client_name: 'Maria Janssen',
    deferral_reason: 'Maria gaf tijdens het laatste gesprek aan dat ze de investering nog met haar partner wil bespreken. Een herziene offerte sturen voor dat gesprek heeft plaatsgevonden kan als druk aanvoelen.',
    recommended_next_action: 'Stuur een kort, laagdrempelig bericht met de vraag of ze het gesprek hebben kunnen voeren en of er vragen zijn.',
    recommended_trigger: 'Dinsdagochtend — 2 dagen van nu',
    outcome_validated: null,
    outcome_notes: null,
    partner_id: 'partner-003',
  },
];

const MOCK_CORRECTIONS: SemmyCorrection[] = [
  {
    id: 'corr-1',
    created_at: da(1),
    action_id: 'sc-4',
    original_text: 'Geachte mevrouw Smit, ik neem contact op om te informeren of u reeds de kans heeft gehad de offerte te bekijken die wij u hebben toegezonden.',
    approved_text: 'Beste Lisa, heb je al even de kans gehad om de offerte te bekijken? Laat me even weten als je vragen hebt — ik help je graag!',
    diff: 'Formal → informal register. Shortened. Added warmth ("ik help je graag").',
    correction_category: 'tone',
    partner_id: 'partner-003',
  },
];

// Authority tracking for all seven action types.
// scheduling is already autonomous — it earned it.
// follow_up is 45/50 consecutive clean approvals — almost there.
const MOCK_AUTHORITY: SemmyAuthority[] = [
  { id: 'auth-1', action_type: 'scheduling',             total_proposed: 67, approved_without_edit: 61, approved_with_edit: 4, rejected: 2, consecutive_clean_approvals: 61, authority_proposal_threshold: 50, authority_status: 'autonomous',   partner_id: 'partner-003' },
  { id: 'auth-2', action_type: 'follow_up',              total_proposed: 51, approved_without_edit: 45, approved_with_edit: 4, rejected: 2, consecutive_clean_approvals: 45, authority_proposal_threshold: 50, authority_status: 'supervised',   partner_id: 'partner-003' },
  { id: 'auth-3', action_type: 'general_communication',  total_proposed: 22, approved_without_edit: 18, approved_with_edit: 3, rejected: 1, consecutive_clean_approvals: 18, authority_proposal_threshold: 50, authority_status: 'supervised',   partner_id: 'partner-003' },
  { id: 'auth-4', action_type: 'quote_communication',    total_proposed: 14, approved_without_edit: 8,  approved_with_edit: 5, rejected: 1, consecutive_clean_approvals: 5,  authority_proposal_threshold: 50, authority_status: 'supervised',   partner_id: 'partner-003' },
  { id: 'auth-5', action_type: 'first_contact',          total_proposed: 38, approved_without_edit: 34, approved_with_edit: 3, rejected: 1, consecutive_clean_approvals: 34, authority_proposal_threshold: 50, authority_status: 'supervised',   partner_id: 'partner-003' },
  { id: 'auth-6', action_type: 'complaint_response',     total_proposed: 6,  approved_without_edit: 3,  approved_with_edit: 3, rejected: 0, consecutive_clean_approvals: 3,  authority_proposal_threshold: 50, authority_status: 'supervised',   partner_id: 'partner-003' },
  { id: 'auth-7', action_type: 'relationship_close',     total_proposed: 2,  approved_without_edit: 1,  approved_with_edit: 1, rejected: 0, consecutive_clean_approvals: 1,  authority_proposal_threshold: 50, authority_status: 'supervised',   partner_id: 'partner-003' },
];

// ── Correction type detection ──────────────────────────────────────────────────

function detectCorrectionType(original: string, corrected: string): string {
  const origLen = original.length;
  const corrLen = corrected.length;
  if (corrLen < origLen * 0.6) return "shortened";
  if (corrLen > origLen * 1.4) return "expanded";
  if (corrected.includes("€") && !original.includes("€")) return "pricing_added";
  if (original.includes("[OFFERTE_VERSTUREN]")) return "quote_correction";
  const formalMarkers = ["u ", "uw ", "beste ", "geachte "];
  const origFormal = formalMarkers.some(m => original.toLowerCase().includes(m));
  const corrFormal = formalMarkers.some(m => corrected.toLowerCase().includes(m));
  if (origFormal !== corrFormal) return "tone_shift";
  return "content_change";
}

// ── Mock conversation threads (visual testing fallback) ───────────────────────
// TODO: remove mock when semmy_conversations has real data in this environment
const MOCK_THREADS: Record<string, { role: string; content: string; created_at: string }[]> = {
  'client-marco': [
    { role: 'client', content: 'Hallo, ik heb een vraag over een PVC vloer voor mijn woonkamer van ongeveer 42m².', created_at: new Date(Date.now() - 900000).toISOString() },
    { role: 'semmy', content: 'Goedemiddag! Vertel me meer — welke kleur en uitvoering spreekt u aan? En wilt u ook de hal meenemen?', created_at: new Date(Date.now() - 840000).toISOString() },
    { role: 'client', content: 'Ja, de hal ook. Ik heb graag houtlook, niet te donker.', created_at: new Date(Date.now() - 780000).toISOString() },
  ],
  'client-hans': [
    { role: 'client', content: 'Dit is echt niet acceptabel. De vloer had al 3 weken geleden gelegd moeten zijn!', created_at: new Date(Date.now() - 7200000).toISOString() },
    { role: 'semmy', content: 'Beste Hans, ik begrijp uw frustratie volledig. Ik ga dit direct uitzoeken voor u.', created_at: new Date(Date.now() - 7140000).toISOString() },
    { role: 'client', content: 'Ik wil gewoon duidelijkheid. Wanneer komt die vloer?', created_at: new Date(Date.now() - 7080000).toISOString() },
  ],
  'client-sandra': [
    { role: 'client', content: 'Kunt u mij een offerte sturen voor een eiken PVC vloer, 48m², inclusief legservice?', created_at: new Date(Date.now() - 3600000).toISOString() },
    { role: 'semmy', content: 'Zeker! Even controleren — wilt u ook een ondervloer meenemen in de offerte?', created_at: new Date(Date.now() - 3540000).toISOString() },
    { role: 'client', content: 'Ja, graag alles inclusief. En 5 jaar garantie als dat kan.', created_at: new Date(Date.now() - 3480000).toISOString() },
  ],
};

// ── Hook ──────────────────────────────────────────────────────────────────────

function mapAction(row: Record<string, unknown>): SemmyAction {
  return {
    id:               String(row.id),
    created_at:       String(row.created_at),
    updated_at:       String(row.updated_at || row.created_at),
    action_type:      row.action_type as ActionType,
    status:           row.status as ActionStatus,
    draft_content:    String(row.draft_content || ''),
    inbound_content:  row.inbound_content ? String(row.inbound_content) : null,
    approved_content: row.approved_content ? String(row.approved_content) : null,
    client_id:        String(row.client_id),
    client_name:      String(row.client_name),
    client_email:     row.client_email ? String(row.client_email) : null,
    client_status:    (row.client_status as ClientStatus) || 'active',
    sentiment_score:  Number(row.sentiment_score ?? 0.7),
    stakes_score:     Number(row.stakes_score ?? 0),
    stakes_inputs:    (row.stakes_inputs as StakesInputs) || {} as StakesInputs,
    requires_approval: Boolean(row.requires_approval),
    approval_reason:  row.approval_reason ? String(row.approval_reason) : null,
    autonomous_send:  Boolean(row.autonomous_send),
    approved_at:      row.approved_at ? String(row.approved_at) : null,
    rejected_at:      row.rejected_at ? String(row.rejected_at) : null,
    sent_at:          row.sent_at ? String(row.sent_at) : null,
    rejection_reason: row.rejection_reason ? String(row.rejection_reason) : null,
    trigger_condition: row.trigger_condition ? String(row.trigger_condition) : null,
    scheduled_for:    row.scheduled_for ? String(row.scheduled_for) : null,
    partner_id:       String(row.partner_id),
    facebook_created_at: row.facebook_created_at ? String(row.facebook_created_at) : null,
  };
}

export function useSemmy(partnerId: string | null) {
  const [queued,      setQueued]      = useState<SemmyAction[]>([]);
  const [completed,   setCompleted]   = useState<SemmyAction[]>([]);
  const [deferred,    setDeferred]    = useState<SemmyDeferred[]>([]);
  const [corrections, setCorrections] = useState<SemmyCorrection[]>([]);
  const [authority,   setAuthority]   = useState<SemmyAuthority[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [sendError,   setSendError]   = useState<string | null>(null);
  const [semmyAvatarUrl, setSemmyAvatarUrl] = useState<string | null>(null);

  const loadActions = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      if (!isSupabaseConfigured) throw new Error('not configured');

      const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString();

      const [actionsRes, deferredRes, correctionsRes, authorityRes] = await Promise.all([
        supabase.from('semmy_actions').select('*').eq('partner_id', partnerId).order('created_at', { ascending: false }),
        supabase.from('semmy_deferred').select('*').eq('partner_id', partnerId).order('created_at', { ascending: false }),
        supabase.from('semmy_corrections').select('*').eq('partner_id', partnerId).order('created_at', { ascending: false }),
        supabase.from('semmy_authority').select('*').eq('partner_id', partnerId),
      ]);

      const { data: opCtx } = await supabase
        .from('semmy_operator_context')
        .select('semmy_avatar_url')
        .eq('partner_id', partnerId)
        .maybeSingle();
      setSemmyAvatarUrl(opCtx?.semmy_avatar_url ?? null);

      if (actionsRes.error) throw actionsRes.error;

      const allActions = (actionsRes.data || []).map(r => mapAction(r as Record<string, unknown>));
      setQueued(allActions.filter(a => a.status === 'queued'));
      setCompleted(allActions.filter(a =>
        (a.status === 'sent' || a.status === 'rejected') &&
        a.created_at >= sevenDaysAgo
      ));
      setDeferred((deferredRes.data || []) as SemmyDeferred[]);
      setCorrections((correctionsRes.data || []) as SemmyCorrection[]);
      setAuthority((authorityRes.data || []) as SemmyAuthority[]);
    } catch {
      setQueued(MOCK_QUEUED.filter(a => a.partner_id === partnerId));
      setCompleted(MOCK_COMPLETED.filter(a => a.partner_id === partnerId));
      setDeferred(MOCK_DEFERRED.filter(d => d.partner_id === partnerId));
      setCorrections(MOCK_CORRECTIONS.filter(c => c.partner_id === partnerId));
      setAuthority(MOCK_AUTHORITY.filter(a => a.partner_id === partnerId));
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => { loadActions(); }, [loadActions]);

  useEffect(() => {
    if (!partnerId || !isSupabaseConfigured) return;

    const channel = supabase
      .channel(`semmy-actions-${partnerId}`)
      .on(
        'postgres_changes',
        {
          event:  'INSERT',
          schema: 'public',
          table:  'semmy_actions',
          filter: `partner_id=eq.${partnerId}`,
        },
        () => { loadActions(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [partnerId, loadActions]);

  // Optimistically mark an action approved and move it to completed — does NOT call semmy-outbound yet.
  // Call triggerSend() after the undo window has elapsed, or requeue() to cancel.
  const markApproved = useCallback(async (actionId: string) => {
    const now = new Date().toISOString();
    const action = queued.find(a => a.id === actionId);
    if (!action) return;
    setSendError(null);

    const approved: SemmyAction = {
      ...action,
      status: 'sent',
      approved_content: action.draft_content,
      approved_at: now,
      sent_at: now,
    };
    setQueued(prev => prev.filter(a => a.id !== actionId));
    setCompleted(prev => [approved, ...prev]);

    setAuthority(prev => prev.map(auth => {
      if (auth.action_type !== action.action_type) return auth;
      const newConsec = auth.consecutive_clean_approvals + 1;
      return {
        ...auth,
        total_proposed: auth.total_proposed + 1,
        approved_without_edit: auth.approved_without_edit + 1,
        consecutive_clean_approvals: newConsec,
        authority_status:
          newConsec >= auth.authority_proposal_threshold && auth.authority_status === 'supervised'
            ? 'proposal_queued'
            : auth.authority_status,
      };
    }));

    if (!isSupabaseConfigured) return;

    await supabase.from('semmy_actions').update({
      approved_content: action.draft_content,
      status: 'approved',
      approved_at: now,
    }).eq('id', actionId);

    supabase.from('semmy_outcomes').insert({
      partner_id:        action.partner_id,
      client_id:         action.client_id,
      related_action_id: actionId,
      outcome_type:      'draft_approved_unchanged',
      outcome_notes:     'Operator approved draft without edits',
      semmy_involved:    true,
      signal_status:     'approved',
    }).then(() => {}).catch(() => {});
  }, [queued]);

  // Optimistically mark an action rejected locally — does NOT write to DB yet.
  // Call triggerReject() after the undo window, or requeue() to cancel.
  const markRejectedLocal = useCallback((actionId: string, reason?: string) => {
    const now = new Date().toISOString();
    const action = queued.find(a => a.id === actionId);
    if (!action) return;

    const rejected: SemmyAction = {
      ...action, status: 'rejected', rejected_at: now,
      rejection_reason: reason || null,
    };
    setQueued(prev => prev.filter(a => a.id !== actionId));
    setCompleted(prev => [rejected, ...prev]);

    setAuthority(prev => prev.map(auth => {
      if (auth.action_type !== action.action_type) return auth;
      return {
        ...auth,
        total_proposed: auth.total_proposed + 1,
        rejected: auth.rejected + 1,
        consecutive_clean_approvals: 0,
      };
    }));
  }, [queued]);

  // Call after the undo window — writes status='rejected' to Supabase.
  const triggerReject = useCallback(async (actionId: string, reason?: string) => {
    if (!isSupabaseConfigured) return;
    const now = new Date().toISOString();
    await supabase.from('semmy_actions').update({
      status: 'rejected', rejected_at: now, rejection_reason: reason || null,
    }).eq('id', actionId);
  }, []);

  // Call after the undo window — invokes semmy-outbound to actually send the message.
  const triggerSend = useCallback(async (actionId: string) => {
    if (!partnerId || !isSupabaseConfigured) return;
    try {
      const result = await supabase.functions.invoke('semmy-outbound', {
        body: { action_id: actionId, partner_id: partnerId },
      });
      if ((result as any)?.error) {
        const message = (result as any).error?.message || JSON.stringify((result as any).error);
        setSendError(`Outbound-fout: ${message}`);
      } else {
        setSendError(null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setSendError(`Outbound-fout: ${message}`);
    }
  }, [partnerId]);

  // Undo an approval — moves the action back to the queue before semmy-outbound fires.
  const requeue = useCallback((actionId: string) => {
    const action = completed.find(a => a.id === actionId);
    if (!action) return;
    const reverted: SemmyAction = {
      ...action,
      status: 'queued',
      approved_content: null,
      approved_at: null,
      sent_at: null,
    };
    setCompleted(prev => prev.filter(a => a.id !== actionId));
    setQueued(prev => [reverted, ...prev]);
    if (isSupabaseConfigured) {
      supabase.from('semmy_actions')
        .update({ status: 'queued', approved_content: null, approved_at: null })
        .eq('id', actionId).then(() => {}).catch(() => {});
    }
  }, [completed]);

  // Approve with edits — breaks the clean streak, logs the correction
  const approveWithEdit = useCallback(async (
    actionId: string,
    editedContent: string,
    correctionCategory: string,
    correctionNote?: string,
  ) => {
    const now = new Date().toISOString();
    const action = queued.find(a => a.id === actionId);
    if (!action) return;
    setSendError(null);

    const origLen = action.draft_content.length;
    const corrLen = editedContent.length;
    const wordDelta = Math.abs(
      editedContent.split(/\s+/).length - action.draft_content.split(/\s+/).length
    );
    const wasSignificant = wordDelta > 5 || corrLen < origLen * 0.5;
    const correctionType = detectCorrectionType(action.draft_content, editedContent);

    const correction: SemmyCorrection = {
      id: `corr-${Date.now()}`,
      created_at: now,
      action_id: actionId,
      original_text: action.draft_content,
      approved_text: editedContent,
      diff: `Category: ${correctionCategory}`,
      correction_category: correctionCategory,
      partner_id: action.partner_id,
      client_id: action.client_id,
      correction_note: correctionNote,
      correction_type: correctionType,
      was_significant: wasSignificant,
    };

    const approved: SemmyAction = {
      ...action,
      status: 'sent',
      approved_content: editedContent,
      approved_at: now,
      sent_at: now,
    };

    setQueued(prev => prev.filter(a => a.id !== actionId));
    setCompleted(prev => [approved, ...prev]);
    setCorrections(prev => [correction, ...prev]);

    // Edit resets the consecutive clean streak
    setAuthority(prev => prev.map(auth => {
      if (auth.action_type !== action.action_type) return auth;
      return {
        ...auth,
        total_proposed: auth.total_proposed + 1,
        approved_with_edit: auth.approved_with_edit + 1,
        consecutive_clean_approvals: 0,
      };
    }));

    if (!isSupabaseConfigured) return;

    // Update action + log correction in parallel, then call semmy-outbound
    await Promise.all([
      supabase.from('semmy_actions').update({
        approved_content: editedContent,
        status: 'approved',
        approved_at: now,
      }).eq('id', actionId),
      supabase.from('semmy_corrections').insert({
        action_id:         actionId,
        original_text:     action.draft_content,
        approved_text:     editedContent,
        diff:              `Category: ${correctionCategory}`,
        correction_category: correctionCategory,
        partner_id:        action.partner_id,
        client_id:         action.client_id,
        correction_note:   correctionNote || null,
        correction_type:   correctionType,
        was_significant:   wasSignificant,
      }),
    ]);

    try {
      console.log('useSemmy: invoking semmy-outbound with edited content', { actionId, partnerId: action.partner_id });
      const result = await supabase.functions.invoke('semmy-outbound', {
        body: { action_id: actionId, partner_id: action.partner_id },
      });
      if ((result as any)?.error) {
        const message = (result as any).error?.message || JSON.stringify((result as any).error);
        setSendError(`Outbound-fout: ${message}`);
        console.error('useSemmy: semmy-outbound invoke returned error', { actionId, error: (result as any).error });
      } else {
        setSendError(null);
        console.log('useSemmy: semmy-outbound invoked successfully', { actionId });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setSendError(`Outbound-fout: ${message}`);
      console.error('useSemmy: semmy-outbound invoke failed', { actionId, error: err });
    }
  }, [queued]);

  // Reject an action — resets the clean streak
  const reject = useCallback(async (actionId: string, reason?: string) => {
    const now = new Date().toISOString();
    const action = queued.find(a => a.id === actionId);
    if (!action) return;

    const rejected: SemmyAction = {
      ...action, status: 'rejected', rejected_at: now,
      rejection_reason: reason || null,
    };
    setQueued(prev => prev.filter(a => a.id !== actionId));
    setCompleted(prev => [rejected, ...prev]);

    setAuthority(prev => prev.map(auth => {
      if (auth.action_type !== action.action_type) return auth;
      return {
        ...auth,
        total_proposed: auth.total_proposed + 1,
        rejected: auth.rejected + 1,
        consecutive_clean_approvals: 0,
      };
    }));

    if (!isSupabaseConfigured) return;
    await supabase.from('semmy_actions').update({
      status: 'rejected', rejected_at: now, rejection_reason: reason || null,
    }).eq('id', actionId);
  }, [queued]);

  // Accept an autonomy proposal for an action type
  const grantAutonomy = useCallback(async (actionType: ActionType) => {
    setAuthority(prev => prev.map(auth =>
      auth.action_type === actionType
        ? { ...auth, authority_status: 'autonomous' }
        : auth
    ));
    if (!isSupabaseConfigured) return;
    await supabase.from('semmy_authority')
      .update({ authority_status: 'autonomous' })
      .eq('partner_id', partnerId).eq('action_type', actionType);
  }, [partnerId]);

  // Look up corrections for a given action id (for rendering in completed column)
  const correctionFor = useCallback((actionId: string) =>
    corrections.find(c => c.action_id === actionId) || null,
  [corrections]);

  // Load the last 5 conversation turns for a given client (chronological order)
  const loadClientThread = useCallback(async (
    clientId: string | null,
  ): Promise<{ role: string; content: string; created_at: string }[]> => {
    if (!partnerId || !clientId) return [];

    if (!isSupabaseConfigured) {
      // TODO: remove mock when semmy_conversations has real data in this environment
      return MOCK_THREADS[clientId] ?? [];
    }

    try {
      const { data, error } = await supabase
        .from('semmy_conversations')
        .select('role, content, created_at')
        .eq('partner_id', partnerId)
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        console.error('loadClientThread error:', error.message);
        return MOCK_THREADS[clientId] ?? [];
      }

      const rows = data ?? [];
      if (rows.length === 0) {
        // TODO: remove mock when semmy_conversations has real data in this environment
        return MOCK_THREADS[clientId] ?? [];
      }

      return (rows as { role: string; content: string; created_at: string }[]).reverse();
    } catch (err) {
      console.error('loadClientThread exception:', err);
      return [];
    }
  }, [partnerId]);

  const loadPerformance = useCallback(async (weekOffset = 0): Promise<SemmyPerformance> => {
    const empty: SemmyPerformance = {
      enquiries_this_week: 0, approved: 0, autonomous: 0,
      quotes_sent: 0, corrections_this_week: 0,
      approval_rate: 0, autonomous_rate: 0,
    };
    if (!partnerId || !isSupabaseConfigured) return empty;

    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const rangeStart = new Date(now + (weekOffset - 1) * msPerWeek).toISOString();
    const rangeEnd   = weekOffset < 0 ? new Date(now + weekOffset * msPerWeek).toISOString() : null;

    const applyRange = (q: ReturnType<typeof supabase.from>) => {
      let query = (q as any).gte('created_at', rangeStart);
      if (rangeEnd) query = query.lt('created_at', rangeEnd);
      return query;
    };

    const [actionsRes, quotesRes, correctionsRes] = await Promise.all([
      applyRange(supabase.from('semmy_actions').select('status, autonomous_send').eq('partner_id', partnerId)),
      applyRange(supabase.from('quotes').select('id', { count: 'exact', head: true }).eq('partner_id', partnerId)),
      applyRange(supabase.from('semmy_corrections').select('id', { count: 'exact', head: true }).eq('partner_id', partnerId)),
    ]);

    const actions = (actionsRes.data ?? []) as { status: string; autonomous_send: boolean }[];
    const total = actions.length;
    const approved = actions.filter(a => a.status === 'approved' || a.status === 'sent').length;
    const autonomous = actions.filter(a =>
      a.autonomous_send && (a.status === 'approved' || a.status === 'sent')
    ).length;

    return {
      enquiries_this_week: total,
      approved,
      autonomous,
      quotes_sent: quotesRes.count ?? 0,
      corrections_this_week: correctionsRes.count ?? 0,
      approval_rate: total > 0 ? Math.round((approved / total) * 100) : 0,
      autonomous_rate: total > 0 ? Math.round((autonomous / total) * 100) : 0,
    };
  }, [partnerId]);

  return {
    queued, completed, deferred, corrections, authority, loading, sendError,
    loadActions,
    markApproved, triggerSend, requeue,
    markRejectedLocal, triggerReject,
    approveWithEdit, reject, grantAutonomy, correctionFor, loadClientThread, loadPerformance,
    semmyAvatarUrl,
  };
}
